import { useState } from 'react';

const TIERS = [
  {
    id: 'starter' as const,
    name: 'Starter',
    price: '$49',
    period: '/month',
    desc: 'For PMO teams running their first structured risk assessments.',
    features: [
      'Up to 5 users',
      '10 active projects',
      'All 4 assessment dimensions',
      'Category 1–4 governance output',
      'Shareable assessment links',
      'Email support',
    ],
    cta: 'Start 14-Day Trial',
    featured: false,
  },
  {
    id: 'pro' as const,
    name: 'Pro',
    price: '$99',
    period: '/month',
    desc: 'Portfolio view, heatmaps, and full audit coverage for growing PMOs.',
    features: [
      'Up to 25 users',
      'Unlimited projects',
      'Portfolio dashboard',
      'Phase heatmap & risk matrix',
      'Audit-ready PDF export',
      'SSO (Google / Microsoft)',
      'Priority support',
    ],
    cta: 'Start 14-Day Trial',
    featured: true,
  },
  {
    id: 'enterprise' as const,
    name: 'Enterprise',
    price: '$399',
    period: '/month',
    desc: 'Custom governance controls for government agencies and large IT departments.',
    features: [
      'Unlimited users',
      'Multi-department scoping',
      'Custom scoring templates',
      'Full audit trail',
      'SLA guarantee',
      'Dedicated PMO success manager',
    ],
    cta: 'Start 14-Day Trial',
    featured: false,
  },
];

export default function BillingPage() {
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleCheckout(tier: 'starter' | 'pro' | 'enterprise') {
    setLoading(tier);
    setError(null);
    try {
      const res = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tier,
          successUrl: `${window.location.origin}/billing/success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/billing`,
        }),
      });

      if (!res.ok) {
        const data = await res.json() as { error?: string };
        throw new Error(data.error ?? `HTTP ${res.status}`);
      }

      const { url } = await res.json() as { url: string };
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start checkout. Please try again.');
      setLoading(null);
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', padding: '64px 24px' }}>
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <h1 style={{ fontSize: 36, fontWeight: 700, color: '#111', margin: '0 0 12px' }}>
            Choose your plan
          </h1>
          <p style={{ color: '#6b7280', fontSize: 16 }}>
            14-day free trial on all plans. No credit card required to start.
          </p>
          {error && (
            <p style={{ color: '#dc2626', marginTop: 16, fontSize: 14 }}>{error}</p>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
          {TIERS.map((tier) => (
            <div
              key={tier.id}
              style={{
                background: tier.featured ? '#111827' : '#fff',
                border: tier.featured ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                borderRadius: 12,
                padding: '32px 28px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {tier.featured && (
                <div style={{ color: '#3b82f6', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 12 }}>
                  Most Popular
                </div>
              )}
              <div style={{ color: tier.featured ? '#f9fafb' : '#111', fontWeight: 700, fontSize: 20, marginBottom: 4 }}>{tier.name}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 8 }}>
                <span style={{ color: tier.featured ? '#f9fafb' : '#111', fontSize: 40, fontWeight: 800 }}>{tier.price}</span>
                <span style={{ color: tier.featured ? '#9ca3af' : '#6b7280', fontSize: 14 }}>{tier.period}</span>
              </div>
              <p style={{ color: tier.featured ? '#d1d5db' : '#6b7280', fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>{tier.desc}</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px', flex: 1 }}>
                {tier.features.map((f) => (
                  <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10, fontSize: 14, color: tier.featured ? '#e5e7eb' : '#374151' }}>
                    <span style={{ color: '#10b981', flexShrink: 0, marginTop: 1 }}>✓</span>
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => void handleCheckout(tier.id)}
                disabled={loading !== null}
                style={{
                  background: tier.featured ? '#3b82f6' : 'transparent',
                  border: tier.featured ? 'none' : '1px solid #d1d5db',
                  borderRadius: 8,
                  color: tier.featured ? '#fff' : '#374151',
                  cursor: loading !== null ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  opacity: loading !== null ? 0.7 : 1,
                  padding: '12px 24px',
                  transition: 'opacity 0.15s',
                  width: '100%',
                }}
              >
                {loading === tier.id ? 'Redirecting…' : tier.cta}
              </button>
            </div>
          ))}
        </div>

        <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, marginTop: 40 }}>
          Cancel any time. Invoices sent monthly. Questions?{' '}
          <a href="mailto:support@rca.morton-tech.com" style={{ color: '#3b82f6' }}>Contact support</a>
        </p>
      </div>
    </div>
  );
}
